import { assertIsNotUndefined } from "common/utils/assertOfType";
import type { Texture, WebGLRenderer, TypedArray } from "three";
import {
  Box3,
  DataArrayTexture,
  DataTexture,
  LinearFilter,
  LinearMipmapLinearFilter,
  RedFormat,
  RepeatWrapping,
  UnsignedByteType,
  Vector3,
} from "three";
import type { ExtendedTexture, ExtendedWebGLRendererGenerateTexture3DLayerMipmap } from "../threejs/three-extensions";
import { TEXTURE_ATLAS_TEXTURE_WIDTH } from "./textureAtlasConstants";
import { textureNames } from "./textureNames";

export interface TextureAtlas {
  textureAtlas: DataArrayTexture | undefined;
  atlasLoaded: Set<string>;
}

const atlasTextures: { [name: string]: Texture } = {};

const atlasMap: { [name: string]: number } = {};

let freeAtlasIndex: number = 0;

const rendererToAtlas: WeakMap<WebGLRenderer, TextureAtlas> = new WeakMap();

function getAtlasWithMetadata(renderer: WebGLRenderer): TextureAtlas {
  let atlas: TextureAtlas | undefined = rendererToAtlas.get(renderer);
  if (atlas === undefined) {
    atlas = {
      textureAtlas: undefined,
      atlasLoaded: new Set(),
    };
    rendererToAtlas.set(renderer, atlas);
  }
  return atlas;
}

export function atlasTextureForRenderer(
  renderer: WebGLRenderer,
  initializeAtlasMemory: boolean,
  overrideCapacityForTesting?: number,
): DataArrayTexture {
  const atlas = getAtlasWithMetadata(renderer);
  const textureCapacity = overrideCapacityForTesting ?? atlas.textureAtlas?.image?.depth ?? textureNames.length;
  if (atlas.textureAtlas === undefined || atlas.textureAtlas.image.depth !== textureCapacity) {
    if (atlas.textureAtlas !== undefined) {
      atlas.textureAtlas.dispose();
      atlas.atlasLoaded = new Set();
    }
    if (initializeAtlasMemory) {
      // Avoid generating WebGL error, at the cost of allocating significantly more memory.
      // This won't be needed with future versions of three.js.
      atlas.textureAtlas = new DataArrayTexture(
        new Uint8Array(TEXTURE_ATLAS_TEXTURE_WIDTH * TEXTURE_ATLAS_TEXTURE_WIDTH * textureCapacity),
        TEXTURE_ATLAS_TEXTURE_WIDTH,
        TEXTURE_ATLAS_TEXTURE_WIDTH,
        textureCapacity,
      );
    } else {
      // The extra call that's done by three.js to upload data to the texture memory generates a GL error, but the erroneous call has no other consequences besides that.
      atlas.textureAtlas = new DataArrayTexture(
        null as any, // TODO: Remove "as any" when three.js type definitions are fixed to allow null here.
        TEXTURE_ATLAS_TEXTURE_WIDTH,
        TEXTURE_ATLAS_TEXTURE_WIDTH,
        textureCapacity,
      );
    }
    atlas.textureAtlas.format = RedFormat;
    atlas.textureAtlas.wrapS = RepeatWrapping;
    atlas.textureAtlas.wrapT = RepeatWrapping;
    atlas.textureAtlas.minFilter = LinearMipmapLinearFilter;
    atlas.textureAtlas.magFilter = LinearFilter;
    atlas.textureAtlas.generateMipmaps = false;
    (atlas.textureAtlas as any as ExtendedTexture).allocateMipmaps = true;
    atlas.textureAtlas.needsUpdate = true;
    for (const name of Object.keys(atlasTextures)) {
      // Add all previously loaded textures to atlas.
      addToAtlas(renderer, name, atlasTextures[name], false);
    }
  }
  return atlas.textureAtlas;
}

export function atlasSlotFromTextureName(name: string): number {
  if (!atlasMap.hasOwnProperty(name)) {
    atlasMap[name] = freeAtlasIndex;
    ++freeAtlasIndex;
  }
  return atlasMap[name];
}

interface TextureProperties {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  __webglTexture: WebGLTexture;
}

function getWebGLTextureFromTHREE(renderer: WebGLRenderer, tex: Texture): WebGLTexture {
  let webglTex = (renderer.properties.get(tex) as TextureProperties).__webglTexture;
  if (!webglTex) {
    renderer.initTexture(tex);
    webglTex = (renderer.properties.get(tex) as TextureProperties).__webglTexture;
  }
  assertIsNotUndefined(webglTex);
  return webglTex;
}

interface TextureImage {
  width: number;
  height: number;
}

interface DataTextureImage extends TextureImage {
  data: TypedArray;
}

export function addToAtlas(renderer: WebGLRenderer, name: string, tex: Texture, initializeAtlasMemory: boolean) {
  const atlas = getAtlasWithMetadata(renderer);
  if (atlas.atlasLoaded.has(name)) {
    return;
  }
  atlas.atlasLoaded.add(name);
  const texImage = tex.image as TextureImage;
  if (!(texImage.width === TEXTURE_ATLAS_TEXTURE_WIDTH && texImage.height === TEXTURE_ATLAS_TEXTURE_WIDTH)) {
    console.assert(false);
  }

  const atlasTex = atlasTextureForRenderer(renderer, initializeAtlasMemory);
  const atlasSlot = atlasSlotFromTextureName(name);

  if (!(atlasSlot < atlasTex.image?.depth)) {
    console.assert(false, "Should stay below texture atlas capacity");
  }

  // Improves loading times by invoking WebGL 2 directly. Three calls getParameter on the context
  // which can cause a bubble while it waits for the GPU process queue to finish.
  const gl = renderer.getContext();
  if (gl instanceof WebGL2RenderingContext && tex.format === RedFormat && tex.type === UnsignedByteType) {
    const webglAtlasTex = getWebGLTextureFromTHREE(renderer, atlasTex);
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, webglAtlasTex);
    const textureData = tex instanceof DataTexture ? (tex.image as DataTextureImage).data : (tex.image as TypedArray);
    gl.texSubImage3D(
      gl.TEXTURE_2D_ARRAY,
      0,
      0,
      0,
      atlasSlot,
      texImage.width,
      texImage.height,
      1,
      gl.RED,
      gl.UNSIGNED_BYTE,
      textureData,
    );
  } else {
    renderer.copyTextureToTexture(
      tex,
      atlasTex,
      new Box3(new Vector3(0, 0, 0), new Vector3(TEXTURE_ATLAS_TEXTURE_WIDTH - 1, TEXTURE_ATLAS_TEXTURE_WIDTH - 1, 0)),
      new Vector3(0, 0, atlasSlot),
      0,
      0,
    );
  }

  (renderer as ExtendedWebGLRendererGenerateTexture3DLayerMipmap).generateTexture3DLayerMipmap(atlasTex, atlasSlot);
  atlasTextures[name] = tex;
}
